import { useState, useCallback, useMemo, useRef } from 'react';
import {
  PageState,
  InputBarState,
  InputBarStateConfig,
  RegionVisibilityMapping
} from '@/types/VideoPageStateMachine';
import {
  PAGE_STATE_TRANSITIONS,
  INPUT_BAR_STATES,
  REGION_VISIBILITY,
  getRegionStyles,
  getRegionCSSClasses
} from '@/config/videoPageStateMachine';

// 状态机管理Hook返回类型
export interface VideoPageStateMachineReturn {
  // 当前状态
  pageState: PageState;
  inputBarConfig: InputBarStateConfig;
  regionVisibility: RegionVisibilityMapping;

  // 状态转换方法
  transitionTo: (targetState: PageState) => boolean;

  // 业务操作方法
  startGeneration: () => void;
  completeGeneration: () => void;
  stopGeneration: () => void;
  showError: () => void;
  resetToInitial: () => void;

  // 兼容层方法
  getInputBarCompatState: () => boolean;

  // 样式获取方法
  getRegionStyles: (region: 'tutorial' | 'sceneList' | 'resultArea') => ReturnType<typeof getRegionStyles>;
  getRegionCSSClasses: (region: 'tutorial' | 'sceneList' | 'resultArea') => ReturnType<typeof getRegionCSSClasses>;
}

// 状态机管理Hook
export const useVideoPageStateMachine = (): VideoPageStateMachineReturn => {
  const [pageState, setPageState] = useState<PageState>(PageState.INITIAL);

  // 添加一个ref来跟踪当前状态，解决React异步状态更新的问题
  const currentPageStateRef = useRef<PageState>(PageState.INITIAL);

  // 获取当前配置
  const currentStateConfig = useMemo(() =>
    PAGE_STATE_TRANSITIONS[pageState],
    [pageState]
  );

  const inputBarConfig = useMemo(() =>
    INPUT_BAR_STATES[currentStateConfig.inputBarState],
    [currentStateConfig]
  );

  const regionVisibility = useMemo(() =>
    REGION_VISIBILITY[pageState],
    [pageState]
  );

  // 状态转换函数
  const transitionTo = useCallback((targetState: PageState): boolean => {
    // 使用ref获取当前状态，解决React异步状态更新的问题
    const currentState = currentPageStateRef.current as PageState;
    const currentConfig = PAGE_STATE_TRANSITIONS[currentState];
    const allowedStates = currentConfig.allowedNextStates;

    if (!allowedStates.includes(targetState)) {
      console.warn(`[STATE_MACHINE] ❌ Invalid state transition: ${currentState} -> ${targetState}`);
      return false;
    }


    console.log('[STATE_MACHINE] 🔄 Page state transition:', currentState, '->', targetState);
    // 获取并打印inputBar的子状态
    const nextConfig = PAGE_STATE_TRANSITIONS[targetState];
    console.log('[STATE_MACHINE_CHILDREN] 🔄 InputBar state transition:', currentConfig.inputBarState, '->', nextConfig.inputBarState);

    // 🔴 直接更新ref，确保状态立即反映在ref中，解决useEffect异步执行的问题
    currentPageStateRef.current = targetState;
    setPageState(targetState);
    return true;
  }, [currentPageStateRef]);

  // 业务操作函数
  const startGeneration = useCallback(() => {
    console.log('[STATE_MACHINE] 🚀 Starting generation, transitioning to GENERATING state');
    transitionTo(PageState.GENERATING);
  }, [transitionTo]);

  const completeGeneration = useCallback(() => {
    console.log('[STATE_MACHINE] ✅ Generation completed, transitioning to EDITING state');
    transitionTo(PageState.EDITING);
  }, [transitionTo]);

  const stopGeneration = useCallback(() => {
    console.log('[STATE_MACHINE] ⏹️ Generation stopped, transitioning to INITIAL state');
    transitionTo(PageState.INITIAL);
  }, [transitionTo]);

  const showError = useCallback(() => {
    console.log('[STATE_MACHINE] ❌ Generation failed, transitioning to ERROR state');
    transitionTo(PageState.ERROR);
  }, [transitionTo]);

  const resetToInitial = useCallback(() => {
    console.log('[STATE_MACHINE] 🔄 Resetting to INITIAL state');
    transitionTo(PageState.INITIAL);
  }, [transitionTo]);

  // 兼容层方法 - 保持与原有代码的兼容性
  const getInputBarCompatState = useCallback((): boolean => {
    return inputBarConfig.state === InputBarState.PROCESSING || inputBarConfig.state === InputBarState.EDITING;
  }, [inputBarConfig]);



  // 样式获取方法
  const getRegionStylesMethod = useCallback((region: 'tutorial' | 'sceneList' | 'resultArea') => {
    return getRegionStyles(pageState, region);
  }, [pageState]);

  const getRegionCSSClassesMethod = useCallback((region: 'tutorial' | 'sceneList' | 'resultArea') => {
    return getRegionCSSClasses(pageState, region);
  }, [pageState]);

  return {
    // 当前状态
    pageState,
    inputBarConfig,
    regionVisibility,

    // 转换方法
    transitionTo,

    // 业务操作方法
    startGeneration,
    completeGeneration,
    stopGeneration,
    showError,
    resetToInitial,

    // 兼容层方法
    getInputBarCompatState,

    // 样式获取方法
    getRegionStyles: getRegionStylesMethod,
    getRegionCSSClasses: getRegionCSSClassesMethod
  };
};